import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { Dialog, makeStyles } from '@material-ui/core';

import ScreenTextContext from '../../../contexts/ScreenTextContext';
import { TASK_GROUP_TYPES, TASK_TYPES } from '../../../utils/constants';
import { Button } from '../../Button';
import { Scenario } from '../'
import { Feedback, MultipleChoice, Sequence } from './';

const useStyles = makeStyles((theme) => ({
  dialog: {
     '.MuiDialog-root .MuiPaper-rounded ': {
       width: 'auto',
       borderRadius: '2rem'
     }
  },
  paper: {
    width: 'auto !important',
    borderRadius: '2rem !important'
  }
}))

export const TaskContainer = ({completeTask, goToNextTask, isLastRound, isLastTask, task, taskGroup}) => {
  
  const [completed, setCompleted] = useState(false);
  const [completeEnabled, setCompleteEnabled] = useState(false);
  const [correct, setCorrect] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [ canClose, setCanClose ] = useState(false);

  const audioRef = useRef();
  const nextBtnRef = useRef();

  const modalOpenRef = useRef(modalOpen);
  modalOpenRef.current = modalOpen;

  const classes = useStyles();

  // reset for new task
  useEffect(() => {
    setCompleteEnabled(false);
    setCompleted(false);
    setCorrect(false);

    // scroll back to top
    window.scrollTo(0, 0);
    
  }, [task])

  useEffect(() => {
    if (completed) {
      nextBtnRef.current.focus();
    }
  }, [completed])

  // Theoretically, this prevents a double click of the button 
  // from immediately closing the modal and MAYBE causing the weird "skip-scoring" issue
  useEffect(() => {
    if (modalOpen) {
      setTimeout(() => {
        setCanClose(true)
      }, 250)

    }
    
  }, [modalOpen])
  
  const { screenText } = useContext(ScreenTextContext);
  
  const handleSubmit = (e) => {      
    e.preventDefault();
    
    // Theoretically, this can't happen. But we're just going to make sure.
    if (modalOpen) return;

    setModalOpen(true);
    
    audioRef.current.currentTime = 0
    audioRef.current.play();
    
    setTimeout(() => {
      
      handleClose();

    }, 2750)

  }

  const handleClose = useCallback(() => {
    if (modalOpenRef.current) {
      setModalOpen(false);
      setCompleted(true); 
      setCanClose(false)
      completeTask(correct);
    }
  }, [completeTask, correct])

  const getNextText = () => {

    if (isLastRound && isLastTask) {
      return screenText.play.finish;
    } 
    else if (isLastTask) {
      return screenText.play.nextRound;
    } 
    else {
      return screenText.play.next;
    }

  }

  const displayTask = useCallback(() => {
   
    let Task = <></>;

    switch(task.$.type) {
      case (TASK_TYPES.MC):
      case (TASK_TYPES.MCIA): 
      case (TASK_TYPES.MCV): 
        Task= MultipleChoice;
        break;
      case (TASK_TYPES.SEQ):
        Task = Sequence;
        break;
      default:
        /** 
         * @todo 
         * throw/handle error
         */
    }

    return <Task 
            task={task} 
            setCorrect={setCorrect} 
            setCompleteEnabled={setCompleteEnabled} 
            completed={completed}
          />

  }, [task, completed])


  if (!screenText || !screenText.play || !screenText.play.feedback) {
    return '';
  }
  
  return (
    <form onSubmit={handleSubmit}>

      {/* Scenario title/dialog, if applicable */}
      { taskGroup.$.type === TASK_GROUP_TYPES.SCENARIO ? 
        <Scenario scenario={taskGroup} popover />
      : '' }

      <div style={{position: 'fixed', bottom:'10px', right: '10px', color: 'white', fontSize: 14}}>
         {task.$.id}
         <br />
         {task.$.roles}
      </div>
      
      {/* The task itself */}
      {displayTask()}


      {/* Feedback */}
      {completed ? 
        <Feedback>{task.CONSEQUENCE[0]._}</Feedback>
      : '' }
      
      {/* Submit/Next buttons */}
      <div className="task__action">
       
          {/* Submit btn */}
          <Button
            type="submit"
            variant="primary"
            mods={['wide']}
            disabled={!completeEnabled}
            className={!completed ? "" : "d-none"}
          >
            {screenText.play.submit}
          </Button>

          {/* Next btn */}
          <Button
            ref={nextBtnRef}
            type="button"
            variant="primary"
            mods={['wide']}
            onClick={goToNextTask}
            className={completed ? "" : "d-none"}
          >
            {getNextText()} <i className="fas fa-angle-double-right"></i>
          </Button>

      </div>

      {/* Feedback audio file */}
      <div>
        <audio aria-hidden="true" ref={audioRef} src={`${process.env.PUBLIC_URL}/assets/audio/${correct ? 'success' : 'failure'}.wav`} />
      </div>

      {/* Feedback dialog */}
      <Dialog
        BackdropProps={{className:"modal__backdrop"}}
        open={modalOpen}
        onClose={canClose ? handleClose : null}
        PaperProps={{className: classes.paper}}
      >

        <div className={`modal modal--${correct ? 'success' : 'failure'}`}>
          <img src={`${process.env.PUBLIC_URL}/assets/images/responses/${correct ? taskGroup.$.type : task.CONSEQUENCE[0].$.category}.png`} alt="" />
          <div className="modal__text">
            {
              correct ? 
                screenText.play.feedback[taskGroup.$.type]
              :
                screenText.play.feedback[task.CONSEQUENCE[0].$.category] 
            }
          </div>
        </div>

      </Dialog>

    </form>

    
  )
    
}